import { ChevronDownIcon, ChevronRightIcon, FileIcon, FileDirectoryFillIcon } from "@primer/octicons-react";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { rp } from "../api/client";
import { useKnowledge, useTreeAll } from "../api/hooks";
import { cn } from "../lib/utils";

/** A Markdown file; `known` when the knowledge index has it as a document, which the reader can open. */
interface Doc {
  id: string;
  known: boolean;
}

interface Dir {
  name: string;
  path: string;
  dirs: Dir[];
  docs: Doc[];
}

const base = (p: string) => p.slice(p.lastIndexOf("/") + 1);

function build(docs: Doc[]): Dir {
  const root: Dir = { name: "", path: "", dirs: [], docs: [] };
  for (const d of docs) {
    let at = root;
    const parts = d.id.split("/").slice(0, -1);
    parts.forEach((name, i) => {
      const path = parts.slice(0, i + 1).join("/");
      let next = at.dirs.find((x) => x.path === path);
      if (!next) at.dirs.push((next = { name, path, dirs: [], docs: [] }));
      at = next;
    });
    at.docs.push(d);
  }
  return root;
}

/** Every Markdown file in its folder, under the Docs side menu, so all documents can be opened from one place. */
export function DocTree({ repo }: { repo: string }) {
  const q = useKnowledge(repo);
  const files = useTreeAll(repo, "main", true);
  const [params] = useSearchParams();
  const selected = params.get("select");
  const tree = useMemo(() => {
    const known = new Set((q.data?.docs ?? []).map((d) => d.id));
    const paths = new Set<string>(known);
    for (const e of files.data?.entries ?? []) if (e.type === "blob" && /\.(md|mdx|markdown)$/i.test(e.path)) paths.add(e.path);
    return build([...paths].map((id) => ({ id, known: known.has(id) })));
  }, [q.data, files.data]);
  const empty = tree.dirs.length === 0 && tree.docs.length === 0;
  return (
    <nav aria-label="Document files" className="flex min-h-0 flex-col overflow-y-auto border-t border-border-muted pt-1 text-sm first:border-t-0 first:pt-0">
      <h2 className="px-3 py-1.5 text-xs font-semibold uppercase text-muted-foreground">Files</h2>
      {empty ? (
        <p className="px-3 py-1.5 text-xs text-muted-foreground">{files.isLoading || q.isLoading ? "Loading…" : "No Markdown files yet."}</p>
      ) : (
        <Branch repo={repo} dir={tree} depth={0} selected={selected} />
      )}
    </nav>
  );
}

function Branch({ repo, dir, depth, selected }: { repo: string; dir: Dir; depth: number; selected: string | null }) {
  const pad = { paddingLeft: 8 + depth * 14 };
  return (
    <ul>
      {dir.dirs
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((d) => (
          <Folder key={d.path} repo={repo} dir={d} depth={depth} selected={selected} />
        ))}
      {dir.docs
        .sort((a, b) => a.id.localeCompare(b.id))
        .map((d) => (
          <li key={d.id}>
            <Link
              to={d.known ? `${rp(repo)}/docs?view=docs&select=${encodeURIComponent(d.id)}` : `${rp(repo)}/src/main/${d.id}`}
              title={d.known ? d.id : `${d.id} (not a knowledge document: opens in the file view)`}
              style={pad}
              aria-current={selected === d.id ? "page" : undefined}
              className={cn(
                "flex items-center gap-1.5 truncate py-1 pr-2 text-foreground hover:bg-neutral-muted hover:no-underline",
                selected === d.id && "bg-neutral-muted font-semibold",
              )}
            >
              <FileIcon size={14} className="shrink-0 text-muted-foreground" />
              <span className={cn("truncate", !d.known && "text-muted-foreground")}>{base(d.id)}</span>
            </Link>
          </li>
        ))}
    </ul>
  );
}

function Folder({ repo, dir, depth, selected }: { repo: string; dir: Dir; depth: number; selected: string | null }) {
  // Open when it holds the open document; otherwise the reader's choice.
  const holds = selected?.startsWith(`${dir.path}/`) ?? false;
  const [open, setOpen] = useState(true);
  const shown = open || holds;
  return (
    <li>
      <button
        type="button"
        aria-expanded={shown}
        style={{ paddingLeft: 8 + depth * 14 }}
        className="flex w-full items-center gap-1.5 py-1 pr-2 text-left text-foreground hover:bg-neutral-muted"
        onClick={() => setOpen(!shown)}
      >
        {shown ? <ChevronDownIcon size={14} /> : <ChevronRightIcon size={14} />}
        <FileDirectoryFillIcon size={14} className="shrink-0 text-muted-foreground" />
        <span className="truncate">{dir.name}</span>
      </button>
      {shown && <Branch repo={repo} dir={dir} depth={depth + 1} selected={selected} />}
    </li>
  );
}
